import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useSite } from '../store/site'
import { HEALTH_DOT, HEALTH_LABEL } from './ui'

/**
 * Klaviyo account switcher. Reads and writes `siteId` in store/site.js.
 * '' = "All accounts". `compact` renders the trigger for the mobile header.
 */
export default function SiteSwitcher({ compact = false, className = '' }) {
  const { siteId, sites, setSiteId } = useSite()
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const nav = useNavigate()
  const current = sites.find((s) => s.id === siteId) ?? null

  useEffect(() => {
    if (!open) return
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    const onKey = (e) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', onDoc)
    window.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDoc); window.removeEventListener('keydown', onKey) }
  }, [open])

  const pick = (id) => { setSiteId(id); setOpen(false) }

  return (
    <div ref={ref} className={'relative ' + className}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-haspopup="listbox" aria-expanded={open} aria-label="Switch Klaviyo account"
        className={'flex w-full items-center gap-2.5 rounded border border-border bg-white text-left transition-colors hover:border-ink-4 focus:outline-none focus-visible:ring-1 focus-visible:ring-ink-2 ' +
          (compact ? 'h-9 px-2.5 max-w-[190px]' : 'px-3 py-2.5')}>
        <AccountMark site={current} />
        <span className="min-w-0 flex-1">
          <span className={'block truncate font-semibold text-ink ' + (compact ? 'text-xs' : 'text-sm')}>{current ? current.name : 'All accounts'}</span>
          {!compact && <span className="block truncate text-[11px] text-ink-3">{current ? klaviyoLine(current) : `${sites.length} Klaviyo account${sites.length === 1 ? '' : 's'}`}</span>}
        </span>
        <span className="text-ink-4 text-xs" aria-hidden>▾</span>
      </button>

      {open && (
        <div className={'absolute z-40 mt-1 w-[280px] max-w-[calc(100vw-32px)] rounded-lg border border-border bg-white shadow-xl ' + (compact ? 'right-0' : 'left-0 right-0 w-auto')} role="listbox" aria-label="Klaviyo accounts">
          <div className="px-3 pt-2.5 pb-1 text-[10px] font-bold tracking-[0.16em] uppercase text-ink-3">Klaviyo accounts</div>
          <ul className="max-h-72 overflow-y-auto py-1">
            <li>
              <button type="button" role="option" aria-selected={!siteId} onClick={() => pick('')}
                className={'flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm min-h-[44px] hover:bg-surface ' + (!siteId ? 'bg-red-tint/60 text-red-dark font-semibold' : 'text-ink-2')}>
                <span className="inline-flex h-7 w-7 flex-none items-center justify-center rounded bg-ink text-white text-[10px] font-bold">ALL</span>
                <span className="flex-1">All accounts</span>
                {!siteId && <span aria-hidden>✓</span>}
              </button>
            </li>
            {sites.map((s) => (
              <li key={s.id}>
                <button type="button" role="option" aria-selected={s.id === siteId} onClick={() => pick(s.id)}
                  className={'flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm min-h-[44px] hover:bg-surface ' + (s.id === siteId ? 'bg-red-tint/60 text-red-dark font-semibold' : 'text-ink-2')}>
                  <AccountMark site={s} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{s.name}</span>
                    <span className="block truncate text-[11px] font-normal text-ink-3">{klaviyoLine(s)}</span>
                  </span>
                  {s.id === siteId && <span aria-hidden>✓</span>}
                </button>
              </li>
            ))}
            {sites.length === 0 && <li className="px-3 py-3 text-xs text-ink-3">No Klaviyo accounts yet.</li>}
          </ul>
          <div className="border-t border-border p-2 flex gap-2">
            <Link to="/app/accounts" onClick={() => setOpen(false)} className="btn-ghost flex-1 px-3 py-2 text-xs">Manage accounts</Link>
            <button type="button" onClick={() => { setOpen(false); nav('/app/accounts?add=1') }} className="btn-outline flex-1 px-3 py-2 text-xs">Add</button>
          </div>
        </div>
      )}
    </div>
  )
}

function klaviyoLine(s) {
  if (!s.klaviyoConnected) return 'Klaviyo not connected'
  const h = s.health ?? 'unknown'
  return (s.klaviyoAccount?.name || 'Klaviyo connected') + (h !== 'ok' && h !== 'unknown' ? ` · ${HEALTH_LABEL[h].toLowerCase()}` : '')
}

/** Initials tile with a Klaviyo connection dot coloured by health. */
export function AccountMark({ site, size = 'h-7 w-7 text-[11px]' }) {
  if (!site) return <span className={'inline-flex flex-none items-center justify-center rounded bg-ink text-white font-bold ' + size}>ALL</span>
  const initials = site.name.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase()
  const health = site.klaviyoConnected ? (site.health ?? 'unknown') : 'fail'
  const title = site.klaviyoConnected ? `Klaviyo: ${HEALTH_LABEL[health] ?? health}` : 'Klaviyo not connected'
  return (
    <span className={'relative inline-flex flex-none items-center justify-center rounded bg-surface border border-border text-ink font-bold ' + size} title={title}>
      {initials}
      <span className={'absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full ring-2 ring-white ' + (site.klaviyoConnected ? HEALTH_DOT[health] ?? HEALTH_DOT.unknown : 'bg-ink-4')} aria-label={title} />
    </span>
  )
}
